/* eslint-disable @typescript-eslint/indent */
import React, { Component } from 'react';
import styled from 'styled-components';
import Select from 'react-select';
import { DropdownOption, GetDropdownOptionsResponse, themes } from 'kb-shared';
import { Label } from './TextInput/NewTextInput';
import { useQuery } from 'react-apollo';
import { GET_DROPDOWN_OPTIONS } from 'kb-shared/graphql/queries';
import Honeybadger from '@honeybadger-io/js';

const { colors } = themes;

const Container = styled.div`
  display: block;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  max-width: 340px;
  height: 55px;
  max-height: 55px;
  @media screen and (max-width: ${themes.breakpoints.largePhonePortrait}) {
    max-width: 100%;
  }
`;

type Props<T> = {
  options: T[];
  label: string;
  placeholder: string;
  selectedOption: T | null;
  value?: string;
  type?: string;
  error?: boolean;
  className?: string;
  isNew?: boolean;
  name?: string;
  disabled?: boolean;
  onSelect: (item: T) => void;
  extractTitle: (item: T | null) => string;
};

export interface SelectItem {
  original: T;
  label: string;
  value: string;
}

export interface SelectAction {
  name: string;
}

export default class SelectInput<T> extends Component<Props<T>> {
  static defaultProps: Props<unknown> = {
    options: [],
    label: '',
    placeholder: '',
    selectedOption: null,
    isNew: false,
    name: '',
    onSelect: () => {},
    extractTitle: () => ''
  };

  customStyles = {
    // @ts-ignore
    control: (provided, { isFocused, isSelected }) => ({
      ...provided,
      backgroundColor: 'transparent',
      borderRadius: '0',
      border: isSelected ? 'none' : isFocused ? 'none' : 'none',
      cursor: 'pointer',
      boxShadow: 'none',
      borderBottom: isSelected
        ? `solid 1px ${colors.KBYellow}`
        : isFocused
        ? `solid 1px ${colors.KBYellow}`
        : `solid 1px black`,
      '&:hover': {
        borderBottom: `solid 1px ${colors.KBYellow}`
      }
    }),
    // @ts-ignore
    option: (provided, { isFocused, isSelected }) => ({
      ...provided,
      backgroundColor: isSelected ? colors.KBYellow : isFocused ? '#FCF6D6' : 'transparent',
      fontSize: '24px'
    }),
    // @ts-ignore
    dropdownIndicator: (provided, { isFocused, isSelected }) => ({
      ...provided,
      color: isSelected ? colors.KBYellow : isFocused ? colors.KBYellow : null
    }),
    // @ts-ignore
    indicatorSeparator: provided => ({
      ...provided,
      display: 'none'
    }),
    // @ts-ignore
    placeholder: provided => ({
      ...provided,
      fontFamily: 'DomaineDisplay',
      fontDisplay: 'swap',
      fontSize: '24px',
      textAlign: 'left',
      color: '#c6c7ce'
    }),
    // @ts-ignore
    valueContainer: provided => ({
      ...provided,
      fontFamily: 'DomaineDisplay',
      fontDisplay: 'swap',
      fontSize: '24px'
    }),
    // @ts-ignore
    menu: provided => ({
      ...provided,
      maxHeight: '250px'
    }),
    // @ts-ignore
    menuList: provided => ({
      ...provided,
      maxHeight: '250px'
    })
  };

  newStyles = {
    // @ts-ignore
    control: (provided, { isFocused, isSelected }) => ({
      ...provided,
      backgroundColor: 'transparent',
      borderRadius: '4px',
      cursor: 'pointer',
      boxShadow: 'none',
      border: isSelected
        ? `solid 2px ${colors.KBYellow}`
        : isFocused
        ? `solid 2px ${colors.KBYellow}`
        : `solid 1px ${colors.gray}`,
      '&:hover': {
        backgroundColor: colors.KBYellow
      },
      padding: '18px'
    }),
    // @ts-ignore
    option: (provided, { isFocused, isSelected }) => ({
      ...provided,
      backgroundColor: isSelected ? colors.KBYellow : isFocused ? '#FCF6D6' : 'transparent',
      fontSize: '16px',
      borderBottom: `solid 1px ${colors.gray}`,
      borderRadius: '4px',
      padding: '18px',
      fontWeight: 'bolder',
      fontFamily: 'FoundersGroteskText',
      color: colors.night,
      margin: '0'
    }),
    // @ts-ignore
    indicatorSeparator: provided => ({
      ...provided,
      display: 'none'
    }),
    // @ts-ignore
    dropdownIndicator: (provided, { isFocused, isSelected }) => ({
      ...provided,
      color: isSelected ? colors.KBYellow : isFocused ? colors.KBYellow : null
    }),
    // @ts-ignore
    valueContainer: provided => ({
      ...provided,
      fontFamily: 'FoundersGroteskText',
      fontDisplay: 'swap',
      fontSize: '16px',
      fontWeight: 'bolder'
    }),
    // @ts-ignore
    menu: provided => ({
      ...provided,
      position: 'relative',
      zIndex: '101'
    })
  };

  render() {
    const { label, selectedOption, type, name } = this.props;
    const value = this.props.extractTitle(selectedOption);

    const options = this.props.options.map(item => {
      const title = this.props.extractTitle(item);
      return {
        label: title,
        value: title,
        original: item
      };
    });

    const renderSelect = () => {
      return (
        // @ts-ignore
        <Select
          name={name}
          options={options}
          isDisabled={this.props.disabled}
          styles={this.props.isNew ? this.newStyles : this.customStyles}
          onChange={(item: SelectItem, action: SelectAction) =>
            this.props.onSelect(item.original, action)
          }
          placeholder={this.props.placeholder}
          className={this.props.className}
          isSearchable={false}
          value={value !== '' ? { label: value, value: value } : null}
          type={type}
        />
      );
    };

    return (
      <Container>
        <Label htmlFor={label} visible={true}>
          {label}
        </Label>
        {renderSelect()}
      </Container>
    );
  }
}

interface SelectWithOptionsProps {
  lookup_key: string;
  label: string;
  value?: string;
  placeholder?: string;
  onSelect: (item: DropdownOption) => void;
}

export const SelectWithOptions = (props: SelectWithOptionsProps) => {
  const { loading, data, error } = useQuery<GetDropdownOptionsResponse>(GET_DROPDOWN_OPTIONS, {
    variables: { key: props.lookup_key }
  });

  if (error) {
    Honeybadger.notify(error, 'SelectWithOptions: ${props.lookup_key}');
    console.error(error);
  }

  const disabled = !!(loading || error);

  const allOptions = data?.getDropdownOptions || [];
  const selectedOption = allOptions.find(item => item.value === props.value);

  return (
    <SelectInput
      onSelect={props.onSelect}
      options={allOptions}
      label={props.label}
      value={props.value}
      selectedOption={selectedOption}
      placeholder={props.placeholder}
      disabled={disabled}
      extractTitle={item => item?.label || ''}
    />
  );
};
